import {
  applyReadReceipt,
  decodeSafetyBoardPage,
  decodeSafetyBoardReadReceipt,
  decodeSafetyBoardSettings,
  orderedPosts,
  type SafetyBoardCommand,
  type BoardCategoryColor,
  type BoardCategoryIcon,
  type SafetyBoardPage,
  type SafetyBoardPost,
  type SafetyBoardReadReceipt,
  type SafetyBoardSettings,
} from './model';
import { onSensitiveMemoryCacheClear } from '../../runtime/sensitive-cache-lifecycle';
import {
  firstAdministratorRequiredMessage,
  needsFirstAdministrator,
} from '../site-administration-recovery';
import {
  clearWorkspaceHomeReadMemoryCache,
  loadWorkspaceHomeSourceWithFallback,
  type WorkspaceHomeReadPort,
} from '../briefing/workspace-home-read-seam';

const BOARD_FRESH_MS = 10_000;
const BOARD_STALE_MS = 45_000;

/**
 * The board read/write port.
 *
 * `list` is the only read. A page already carries everything a row and the detail dialog
 * render, so there is deliberately no `detail` here: opening a post the caller already holds
 * must not cost a second round trip.
 *
 * `null` from `list` means the page could not be read. It is never an empty board.
 */
export interface SafetyBoardRepository {
  list(limit: number): Promise<SafetyBoardPage | null>;
  settings(): Promise<SafetyBoardSettings | null>;
  settingsForAdministration(): Promise<SafetyBoardSettings>;
  /** Resolves only on a server receipt; the caller un-bolds the row from the receipt, not before. */
  markRead(boardId: string): Promise<SafetyBoardReadReceipt>;
  execute(command: SafetyBoardCommand): Promise<void>;
  updatePolicy(
    input: Readonly<{
      expectedVersion: number;
      categories: readonly Readonly<{
        id: string;
        label: string;
        order: number;
        active: boolean;
        color: BoardCategoryColor;
        icon: BoardCategoryIcon;
      }>[];
      maxImages: number;
    }>,
  ): Promise<void>;
}

/**
 * Used when the board transport is not configured for this build. It reports "could not read"
 * and refuses every command, rather than inventing a board or accepting a write it cannot make.
 */
export class UnconfiguredSafetyBoardRepository implements SafetyBoardRepository {
  async list(): Promise<SafetyBoardPage | null> {
    return null;
  }

  async markRead(): Promise<SafetyBoardReadReceipt> {
    throw new Error('게시판을 사용할 수 없습니다.');
  }
  async settings(): Promise<null> {
    return null;
  }
  async settingsForAdministration(): Promise<never> {
    throw new Error('게시판 분류 관리를 사용할 수 없습니다.');
  }

  async execute(): Promise<void> {
    throw new Error('게시판을 사용할 수 없습니다.');
  }
  async updatePolicy(): Promise<void> {
    throw new Error('게시판 분류 관리를 사용할 수 없습니다.');
  }
}

/** Transport shape returned by `@kop/operations-firebase-adapter`'s `createSafetyBoardPort`. */
type SafetyBoardCallablePort = {
  currentIdentityId?(): string;
  list(limit: number): Promise<unknown>;
  settings(): Promise<unknown>;
  markRead(boardId: string): Promise<unknown>;
  execute(input: Readonly<Record<string, unknown>>): Promise<unknown>;
  executePolicy(input: Readonly<Record<string, unknown>>): Promise<unknown>;
};

const commandFailures: Readonly<Record<string, string>> = {
  IMMUTABLE_RECORD: '발행된 글은 수정할 수 없습니다.',
  VERSION_CONFLICT: '다른 담당자가 먼저 변경했습니다. 새로고침 후 다시 시도해 주세요.',
  IDEMPOTENCY_CONFLICT: '동일한 요청이 이미 처리되었습니다.',
  FORBIDDEN: '이 작업에 대한 권한이 없습니다.',
  SCOPE_MISMATCH: '허용되지 않은 범위입니다.',
  INVALID_STATE: '현재 상태에서는 실행할 수 없습니다.',
  NOT_FOUND: '대상을 찾지 못했습니다.',
  VALIDATION_FAILED: '입력값을 확인해 주세요.',
  INVALID_BOARD_POST: '분류 또는 공개 대상을 확인해 주세요.',
  INVALID_BOARD_DISPLAY: '고정·제목 색상 값을 확인해 주세요.',
  PIN_LIMIT_REACHED: '상단 고정은 최대 3건까지 가능합니다.',
};

/** Maps the wave's own error codes to the register the rest of the product uses. */
export function safetyBoardFailureMessage(error: unknown): string {
  if (needsFirstAdministrator(error)) return firstAdministratorRequiredMessage;
  const code =
    typeof error === 'object' && error !== null && 'message' in error
      ? String((error as { message: unknown }).message)
      : '';
  for (const [key, message] of Object.entries(commandFailures))
    if (code.includes(key)) return message;
  return '요청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.';
}

/**
 * Wires the board screens to the deployed safety-board-production-wave callables through the
 * shared, already-authenticated Firebase app (App Check attaches automatically because that app
 * owns App Check init). Every command is a genuine round trip; nothing is reported as saved
 * without the server's own receipt, and no result is ever written back to local storage.
 */
export class ProductionSafetyBoardRepository implements SafetyBoardRepository {
  private cacheOwner: string | null = null;
  private readonly pages = new Map<number, { value: SafetyBoardPage | null; readAt: number }>();
  private readonly pageReads = new Map<number, Promise<SafetyBoardPage | null>>();
  private settingsValue: { value: SafetyBoardSettings | null; readAt: number } | null = null;
  private settingsRead: Promise<SafetyBoardSettings | null> | null = null;
  private generation = 0;
  private readonly controllers = new Set<AbortController>();

  constructor(
    private readonly port: SafetyBoardCallablePort,
    private readonly workspaceHomeReadPort?: WorkspaceHomeReadPort,
    private readonly workspaceHomeEnvironment: Readonly<{
      VITE_WORKSPACE_HOME_READ_MODE?: string;
    }> = {},
    private readonly expectedSiteId?: string,
  ) {
    onSensitiveMemoryCacheClear(() => this.clearMemoryCache());
  }

  clearMemoryCache() {
    this.generation += 1;
    for (const controller of this.controllers) controller.abort();
    this.controllers.clear();
    this.pages.clear();
    this.pageReads.clear();
    this.settingsValue = null;
    this.settingsRead = null;
    clearWorkspaceHomeReadMemoryCache();
  }

  private checkSession() {
    if (!this.port.currentIdentityId) return;
    try {
      const owner = this.port.currentIdentityId();
      if (!owner) throw new Error('BOARD_AUTHENTICATED_APP_REQUIRED');
      if (owner !== this.cacheOwner) { this.clearMemoryCache(); this.cacheOwner = owner; }
    } catch (error) {
      this.clearMemoryCache(); this.cacheOwner = null;
      throw error;
    }
  }

  private refreshPage(limit: number) {
    const current = this.pageReads.get(limit);
    if (current) return current;
    const generation = this.generation;
    const controller = new AbortController();
    this.controllers.add(controller);
    const legacy = () => this.port.list(limit).then(decodeSafetyBoardPage);
    const pageRead =
      limit === 6 && this.workspaceHomeReadPort
        ? loadWorkspaceHomeSourceWithFallback({
            environment: this.workspaceHomeEnvironment,
            selectedScopeId: this.expectedSiteId ? `site:${this.expectedSiteId}` : null,
            legacy,
            bff: this.workspaceHomeReadPort,
            source: 'board',
            decode: decodeSafetyBoardPage,
            acceptPartial: true,
          })
        : legacy();
    const pending: Promise<SafetyBoardPage | null> = pageRead
      .then((page) => {
        if (controller.signal.aborted || generation !== this.generation)
          throw new DOMException('Board read was cancelled', 'AbortError');
        if (this.expectedSiteId && page?.settings?.siteId !== this.expectedSiteId)
          throw new Error('BOARD_SITE_MISMATCH');
        this.pages.set(limit, { value: page, readAt: Date.now() });
        if (page?.settings) this.settingsValue = { value: page.settings, readAt: Date.now() };
        return page;
      })
      .catch(() => null)
      .finally(() => {
        this.controllers.delete(controller);
        if (this.pageReads.get(limit) === pending) this.pageReads.delete(limit);
      });
    this.pageReads.set(limit, pending);
    return pending;
  }

  async list(limit: number): Promise<SafetyBoardPage | null> {
    this.checkSession();
    const cached = this.pages.get(limit);
    const age = cached ? Date.now() - cached.readAt : Number.POSITIVE_INFINITY;
    if (cached && age <= BOARD_FRESH_MS) return cached.value;
    if (cached && age <= BOARD_STALE_MS) {
      void this.refreshPage(limit);
      return cached.value;
    }
    return this.refreshPage(limit);
  }

  private refreshSettings() {
    if (this.settingsRead) return this.settingsRead;
    const generation = this.generation;
    const controller = new AbortController();
    this.controllers.add(controller);
    const pending: Promise<SafetyBoardSettings | null> = this.port
      .settings()
      .then((value) => {
        if (controller.signal.aborted || generation !== this.generation)
          throw new DOMException('Board settings read was cancelled', 'AbortError');
        const settings = decodeSafetyBoardSettings(value);
        if (this.expectedSiteId && settings?.siteId !== this.expectedSiteId) throw new Error('BOARD_SITE_MISMATCH');
        this.settingsValue = { value: settings, readAt: Date.now() };
        return settings;
      })
      .catch(() => null)
      .finally(() => {
        this.controllers.delete(controller);
        if (this.settingsRead === pending) this.settingsRead = null;
      });
    this.settingsRead = pending;
    return pending;
  }

  async settings(): Promise<SafetyBoardSettings | null> {
    this.checkSession();
    const cached = this.settingsValue;
    const age = cached ? Date.now() - cached.readAt : Number.POSITIVE_INFINITY;
    if (cached && age <= BOARD_FRESH_MS) return cached.value;
    if (cached && age <= BOARD_STALE_MS) {
      void this.refreshSettings();
      return cached.value;
    }
    return this.refreshSettings();
  }

  async settingsForAdministration(): Promise<SafetyBoardSettings> {
    this.checkSession();
    try {
      const settings = decodeSafetyBoardSettings(await this.port.settings());
      if (!settings) throw new Error('BOARD_SETTINGS_INCOMPLETE');
      if (this.expectedSiteId && settings.siteId !== this.expectedSiteId) throw new Error('BOARD_SITE_MISMATCH');
      return settings;
    } catch (error) {
      if (needsFirstAdministrator(error)) throw new Error(firstAdministratorRequiredMessage);
      throw new Error(safetyBoardFailureMessage(error));
    }
  }

  async markRead(boardId: string): Promise<SafetyBoardReadReceipt> {
    const receipt = decodeSafetyBoardReadReceipt(await this.port.markRead(boardId));
    if (!receipt || receipt.boardId !== boardId) throw new Error('읽음 처리를 확인하지 못했습니다.');
    this.clearMemoryCache();
    return receipt;
  }

  async execute(command: SafetyBoardCommand): Promise<void> {
    await this.port.execute({ ...command });
    this.clearMemoryCache();
  }

  async updatePolicy(
    input: Readonly<{
      expectedVersion: number;
      categories: readonly Readonly<{
        id: string;
        label: string;
        order: number;
        active: boolean;
        color: BoardCategoryColor;
        icon: BoardCategoryIcon;
      }>[];
      maxImages: number;
    }>,
  ): Promise<void> {
    const commandId = crypto.randomUUID();
    await this.port.executePolicy({
      operation: 'policy.upsert',
      commandId,
      idempotencyKey: commandId,
      ...input,
    });
    this.clearMemoryCache();
  }
}

/**
 * Local preview board.
 *
 * It stands in for the wave on the loopback preview build only -- the same place
 * `LocalAuthAdapter` stands in for the identity server -- so the screens can be operated
 * without production credentials. It is unreachable from any build that talks to a real
 * project: `createSafetyBoardRepository` only selects it behind an explicit `demo-*`
 * loopback opt-in.
 */
class LocalPreviewSafetyBoardRepository implements SafetyBoardRepository {
  private page: SafetyBoardPage;

  constructor(seed: readonly SafetyBoardPost[]) {
    const items = orderedPosts(seed);
    this.page = {
      items,
      complete: true,
      totalVisible: items.length,
      unreadVisible: items.filter((item) => !item.read).length,
    };
  }

  async list(limit: number): Promise<SafetyBoardPage> {
    const items = orderedPosts(this.page.items).slice(0, limit);
    return { ...this.page, items, complete: items.length >= this.page.items.length };
  }
  async settings(): Promise<SafetyBoardSettings> {
    return {
      siteId: 'site-eumseong',
      authority: { canManage: true },
      configuration: { ready: true, issue: null, version: 1 },
      categories: [
        {
          id: 'general-notice',
          label: '일반 공지',
          order: 1,
          color: 'blue',
          icon: 'megaphone',
        },
        { id: 'urgent-notice', label: '긴급 공지', order: 2, color: 'red', icon: 'alert' },
        { id: 'incident-case', label: '사고 사례', order: 3, color: 'amber', icon: 'shield' },
      ],
      maxImages: 6,
      maxPinned: 3,
    };
  }
  async settingsForAdministration(): Promise<SafetyBoardSettings> {
    return this.settings();
  }

  async markRead(boardId: string): Promise<SafetyBoardReadReceipt> {
    const target = this.page.items.find((item) => item.boardId === boardId);
    if (!target) throw new Error('NOT_FOUND');
    const receipt: SafetyBoardReadReceipt = {
      boardId,
      read: true,
      readAt: target.readAt ?? new Date().toISOString(),
    };
    this.page = applyReadReceipt(this.page, receipt);
    return receipt;
  }

  async execute(command: SafetyBoardCommand): Promise<void> {
    const current = this.page.items.find((item) => item.boardId === command.boardId) ?? null;
    if (command.action === 'display') {
      if (!current || !command.display) throw new Error('NOT_FOUND');
      if (current.displayVersion !== command.expectedVersion) throw new Error('VERSION_CONFLICT');
      this.replace({
        ...current,
        pinned: command.display.pinned,
        titleColor: command.display.titleColor ?? 'default',
        displayVersion: current.displayVersion + 1,
      });
      return;
    }
    if (command.action === 'post' || command.action === 'create' || command.action === 'update') {
      if (command.action === 'post' && current && current.status !== 'published')
        throw new Error('IMMUTABLE_RECORD');
      if (command.action !== 'post' && current && current.status !== 'open')
        throw new Error('IMMUTABLE_RECORD');
      if ((current?.version ?? 0) !== command.expectedVersion) throw new Error('VERSION_CONFLICT');
      const post = command.post;
      if (!post) throw new Error('VALIDATION_FAILED');
      const now = new Date().toISOString();
      this.replace({
        boardId: command.boardId,
        version: command.expectedVersion + 1,
        status: command.action === 'post' ? 'published' : 'open',
        category: post.category,
        categoryLabel:
          (await this.settings())?.categories.find((item) => item.id === post.category)?.label ??
          post.category,
        title: post.title,
        summary: post.body.slice(0, 180),
        body: post.body,
        audience: post.audience,
        publishedAt: current?.publishedAt ?? null,
        updatedAt: now,
        // Mirrors board.upsert: a command's attachments wholesale-replace the record's rather
        // than merging with what was there before, so an edit that does not resend a prior
        // attachment drops it -- the same behaviour the deployed wave has always had.
        attachmentIds: post.attachments.map((item) => item.manifestId),
        attachments: post.attachments,
        followUpActionId: current?.followUpActionId ?? null,
        pinned: current?.pinned ?? false,
        titleColor: current?.titleColor ?? 'default',
        emphasis: current?.emphasis ?? 'normal',
        displayVersion: current?.displayVersion ?? 0,
        read: true,
        readAt: current?.readAt ?? now,
      });
      return;
    }
    if (!current) throw new Error('NOT_FOUND');
    if (current.version !== command.expectedVersion) throw new Error('VERSION_CONFLICT');
    if (command.action === 'publish' && current.status !== 'open') throw new Error('INVALID_STATE');
    if (command.action === 'archive' && !['published', 'promoted'].includes(current.status))
      throw new Error('INVALID_STATE');
    const now = new Date().toISOString();
    this.replace({
      ...current,
      version: current.version + 1,
      status: command.action === 'publish' ? 'published' : 'archived',
      publishedAt: command.action === 'publish' ? now : current.publishedAt,
      updatedAt: now,
    });
  }

  async updatePolicy(): Promise<void> {
    throw new Error('게시판 분류를 저장할 수 없습니다.');
  }

  private replace(post: SafetyBoardPost) {
    const items = orderedPosts([
      ...this.page.items.filter((item) => item.boardId !== post.boardId),
      post,
    ]);
    this.page = {
      items,
      complete: true,
      totalVisible: items.length,
      unreadVisible: items.filter((item) => !item.read).length,
    };
  }
}

const previewBody = (lines: readonly string[]) => lines.join('\n');

function previewSeed(): readonly SafetyBoardPost[] {
  const day = (index: number) => new Date(Date.UTC(2026, 7, 18 - index, 1, 0, 0)).toISOString();
  const base = {
    audience: 'site',
    attachmentIds: [] as readonly string[],
    attachments: [] as readonly { manifestId: string; sha256: string }[],
    followUpActionId: null,
    displayVersion: 1,
  };
  const rows: readonly Readonly<{
    boardId: string;
    category: string;
    title: string;
    body: string;
    pinned?: boolean;
    titleColor?: SafetyBoardPost['titleColor'];
    read?: boolean;
    status?: string;
    audience?: string;
  }>[] = [
    {
      boardId: 'board-2026-0818-hotwork',
      category: 'urgent-notice',
      title: '8월 화기작업 전면 사전허가 전환',
      titleColor: 'critical',
      pinned: true,
      body: previewBody([
        '적용: 8월 19일 07:00부터 음성공장 전 구역',
        '대상: 용접·절단·연삭 등 화기 발생 작업 전량',
        '조치: 작업 전일 16:00까지 작업허가 상신, 감시인 지정 필수',
        '문의: 안전보건팀 내선 2140',
      ]),
    },
    {
      boardId: 'board-2026-0818-heat',
      category: 'general-notice',
      title: '폭염 특보 기간 옥외작업 시간 조정',
      titleColor: 'warning',
      pinned: true,
      body: previewBody([
        '기간: 8월 18일 ~ 8월 25일',
        '조치: 14:00~16:00 옥외작업 중지, 시간당 15분 휴식',
        '급수·그늘막 위치는 각 구역 게시대 확인',
      ]),
    },
    {
      boardId: 'board-2026-0817-drill',
      category: 'general-notice',
      title: '3분기 비상대피 훈련 일정 확정',
      body: previewBody([
        '일시: 8월 27일 10:30',
        '범위: A동·B동 전 인원, 협력사 상주 인원 포함',
        '집결지: 제1주차장 · 제2주차장',
      ]),
    },
    {
      boardId: 'board-2026-0816-incident',
      category: 'incident-case',
      title: '동종업계 지게차 협착 사고 전파',
      titleColor: 'critical',
      body: previewBody([
        '발생: 8월 12일 타사 물류창고',
        '원인: 후진 시 유도자 미배치',
        '조치: 지게차 이동 구간 유도자 배치 상태 자체 점검 8월 22일까지 제출',
      ]),
    },
    {
      boardId: 'board-2026-0815-legal',
      category: 'legal-change',
      title: '중대재해처벌법 시행령 개정 반영 사항',
      titleColor: 'warning',
      read: true,
      body: previewBody([
        '시행: 9월 1일',
        '변경: 안전보건 관리체계 점검 주기 반기 1회에서 분기 1회로 단축',
        '준비: 9월 점검부터 신규 서식 적용',
      ]),
    },
    {
      boardId: 'board-2026-0814-msds',
      category: 'general-notice',
      title: '물질안전보건자료 게시 상태 일제 점검',
      read: true,
      body: previewBody([
        '기간: 8월 14일 ~ 8월 21일',
        '대상: 도장동·표면처리동 취급 물질 전량',
        '제출: 구역별 점검표 안전보건팀 회신',
      ]),
    },
    {
      boardId: 'board-2026-0813-siren',
      category: 'external-siren',
      title: '음성군 호우 예비특보 발효',
      read: true,
      body: previewBody([
        '발효: 8월 13일 18:00',
        '조치: 옥외 자재 결속, 배수로 점검, 고소작업 중지',
      ]),
    },
    {
      boardId: 'board-2026-0812-education',
      category: 'general-notice',
      title: '분기 정기 안전보건교육 미이수자 안내',
      read: true,
      body: previewBody([
        '기한: 8월 29일',
        '대상: 상반기 미이수 인원 34명',
        '이수: 교육 화면에서 배정된 과정 수강 후 확인 서명',
      ]),
    },
    {
      boardId: 'board-2026-0811-crane',
      category: 'general-notice',
      title: '이동식 크레인 반입 절차 변경',
      read: true,
      body: previewBody([
        '적용: 8월 12일부터',
        '변경: 반입 전 검사증·운전자 자격 사본 사전 제출',
        '제출처: 설비관리 담당',
      ]),
    },
    {
      boardId: 'board-2026-0810-confined',
      category: 'incident-case',
      title: '밀폐공간 산소농도 측정 누락 지적 공유',
      read: true,
      body: previewBody([
        '지적: 8월 8일 정기점검',
        '내용: 작업 전 측정 기록 2건 누락',
        '조치: 측정 기록지 작업허가서 첨부 의무화',
      ]),
    },
    {
      boardId: 'board-2026-0809-cost',
      category: 'general-notice',
      title: '하반기 안전관리비 집행 계획 확정',
      read: true,
      body: previewBody(['확정: 8월 9일', '범위: 보호구·안전시설·교육', '집행: 월별 청구서 기준']),
    },
    {
      boardId: 'board-2026-0808-contractor',
      category: 'general-notice',
      title: '협력사 정기 안전보건 협의체 개최',
      read: true,
      body: previewBody([
        '일시: 8월 20일 14:00',
        '장소: 본관 3층 회의실',
        '안건: 하절기 대책 이행',
      ]),
    },
    {
      boardId: 'board-2026-0807-draft',
      category: 'general-notice',
      title: '추석 연휴 안전점검 계획',
      status: 'open',
      read: true,
      body: previewBody([
        '기간: 9월 24일 ~ 9월 28일',
        '점검: 전기·가스·소방 설비',
        '당직: 구역별 1명 배치',
      ]),
    },
  ];
  return rows.map((row, index) => ({
    ...base,
    boardId: row.boardId,
    version: 2,
    status: row.status ?? 'published',
    category: row.category,
    categoryLabel:
      row.category === 'urgent-notice'
        ? '긴급 공지'
        : row.category === 'incident-case'
          ? '사고 사례'
          : '일반 공지',
    title: row.title,
    summary: row.body.slice(0, 180),
    body: row.body,
    audience: row.audience ?? base.audience,
    publishedAt: row.status === 'open' ? null : day(index),
    updatedAt: day(index),
    pinned: row.pinned === true,
    titleColor: row.titleColor ?? 'default',
    emphasis:
      row.titleColor === 'critical'
        ? ('urgent' as const)
        : row.titleColor === 'warning'
          ? ('important' as const)
          : ('normal' as const),
    read: row.read === true,
    readAt: row.read === true ? day(index) : null,
  }));
}

const loopback = (host: string) => ['127.0.0.1', 'localhost', '::1'].includes(host);

const pending = new Map<string, Promise<SafetyBoardRepository>>();

async function build(siteId: string): Promise<SafetyBoardRepository> {
  if (import.meta.env.VITE_SAFETY_BOARD_DATA_ADAPTER === 'firebase') {
    const config = {
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? '',
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? '',
      appId: import.meta.env.VITE_FIREBASE_APP_ID ?? '',
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? '',
      appCheckSiteKey: import.meta.env.VITE_FIREBASE_APPCHECK_SITE_KEY ?? '',
    };
    if (config.projectId !== 'kop-safety-51efb')
      throw new Error('게시판 Firebase 환경 설정이 완전하지 않습니다.');
    // Constructed lazily here, on first use (component mount, after sign-in) -- never at module
    // scope -- and only via the shared authenticated app that createSafetyBoardPort reuses
    // internally, never a second Firebase app instance.
    const { createSafetyBoardPort } =
      await import('@kop/operations-firebase-adapter');
    return new ProductionSafetyBoardRepository(
      createSafetyBoardPort(config, siteId) as SafetyBoardCallablePort,
      undefined, {}, siteId,
    );
  }
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID ?? '';
  const host = typeof window === 'undefined' ? '' : window.location.hostname;
  if (
    import.meta.env.VITE_LOCAL_PREVIEW === 'true' &&
    projectId.startsWith('demo-') &&
    loopback(host)
  )
    return new LocalPreviewSafetyBoardRepository(previewSeed());
  return new UnconfiguredSafetyBoardRepository();
}

/**
 * Built once per document. The port itself holds no identity: every callable reads the current
 * authenticated user at call time, so one instance serves the whole session.
 */
export function createSafetyBoardRepository(siteId: string | null): Promise<SafetyBoardRepository> {
  if (!siteId || !/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u.test(siteId)) return Promise.resolve(new UnconfiguredSafetyBoardRepository());
  const existing = pending.get(siteId);
  if (existing) return existing;
  const created = build(siteId).catch((error) => {
    pending.delete(siteId);
    throw error;
  });
  pending.set(siteId, created);
  return created;
}
